import {
  searchableComputedProperty,
  payloadComputedProperty,
  SB,
} from '@play-co/replicant';
import type { State } from './State';

/**
 * Computed properties for features that have been removed from the game.
 *
 * Removing a computed property takes two releases, because two versions of the
 * game are live simultaneously during a rollout and dropping a property in the
 * newer version breaks the older one:
 *
 * 1. First release removes every reference to the property — `api.searchPlayers`
 *    `where` / `sort` / `fields` clauses and anything reading it off a result.
 * 2. Second release removes the property from the `computedProperties` Replicant
 *    configuration.
 *
 * This file is where properties live during that window, so feature folders only
 * contain properties for features that still exist. It is the computed-property
 * counterpart to `deprecatedState.ts` and
 * `config/messages/deprecatedMessages.ts`.
 *
 * Definitions here must keep producing the SAME values the deleted code did, so
 * queries still running in the older version keep sorting and filtering
 * correctly. Where a value is now a compile-time constant (because its inputs
 * were removed), the constant it collapsed to is used and the reason recorded.
 *
 * When the second release ships, delete the entry from this file.
 */

/**
 * Inlined from the deleted `fairyForest.getters.ts` — most recently joined
 * event id, or undefined when the player never joined one.
 */
function fairyForestGetLastJoinedEventId(state: State): string | undefined {
  const entries = state.fairyForest?.entries;
  if (!entries) return undefined;
  const eventIds = Object.keys(entries);
  if (eventIds.length === 0) return undefined;
  // Sort the event ids by date - lastest first
  eventIds.sort((a, b) => {
    return entries[b].joinedAt - entries[a].joinedAt;
  });
  // Return the most recent event id
  return eventIds[0];
}

function fairyForestGetLastJoinedEntry(state: State) {
  const eventId = fairyForestGetLastJoinedEventId(state);
  if (!eventId) return undefined;
  return state.fairyForest?.entries?.[eventId];
}

export const deprecatedComputedProperties = {
  /**
   * @deprecated Fairy Forest removed (#11760); drop in the follow-up release.
   * Was `!!features(state)?.fairyForest`, which resolved to a hardcoded `true`.
   */
  fairyForestEnabled: searchableComputedProperty(SB.boolean(), () => true),

  /**
   * @deprecated Fairy Forest removed (#11760); drop in the follow-up release.
   * Was `!!features(state)?.fairyForest`, which resolved to a hardcoded `true`.
   */
  fairyForestEnabledNew: searchableComputedProperty(SB.boolean(), () => true),

  /**
   * @deprecated Fairy Forest removed (#11760); drop in the follow-up release.
   * Last event joined by the user.
   */
  fairyForestEvent: searchableComputedProperty(SB.string(), (state: State) => {
    return fairyForestGetLastJoinedEventId(state) ?? '';
  }),

  /**
   * @deprecated Fairy Forest removed (#11760); drop in the follow-up release.
   * Number of plants in the event.
   */
  fairyForestNumPlants: searchableComputedProperty(
    SB.number(),
    (state: State) => {
      const entry = fairyForestGetLastJoinedEntry(state);
      if (!entry) return 0;
      return Object.values(entry.plants).length;
    },
  ),

  /**
   * @deprecated Fairy Forest removed (#11760); drop in the follow-up release.
   * Number of partners in the event - plants might not have a partner yet.
   * Was a `sort` field in the invite-suggestion search.
   */
  fairyForestNumPartners: searchableComputedProperty(
    SB.number(),
    (state: State) => {
      const entry = fairyForestGetLastJoinedEntry(state);
      if (!entry) return 0;
      return Object.values(entry.plants).filter((plant) => !!plant.partnerId)
        .length;
    },
  ),

  /**
   * @deprecated Fairy Forest removed (#11760); drop in the follow-up release.
   * Number of invites received. Was a `sort` field in the invite-suggestion
   * search, and gated players with more than 15 pending invites.
   */
  fairyForestNumPendingInvites: searchableComputedProperty(
    SB.number(),
    (state: State) => {
      const entry = fairyForestGetLastJoinedEntry(state);
      if (!entry) return 0;
      return Object.keys(entry.invitesReceived).length;
    },
  ),

  /**
   * @deprecated Fairy Forest removed (#11760); drop in the follow-up release.
   * Was `fairyForestCanReceiveInviteOA(state, now)`, whose first step is an
   * ongoing-event lookup against `fairyForestSchedule`. That schedule's last
   * occurrence ended 2025-12-23 and no further one can start, so the getter
   * has returned `false` for every player since then.
   */
  fairyForestCanReceiveInviteOA: payloadComputedProperty(
    SB.boolean(),
    () => false,
  ),
};
